import {
  EVENT_SCHEDULE_CALL_NOTICE_LAG,
  PAYOFF_CALLABILITY_AMOUNT,
  PAYOFF_CALLABILITY_AMOUNT_STEP_SIZE,
  PAYOFF_CALLABILITY_AMOUNT_TYPE,
  PAYOFF_CALLABILITY_BARRIER_LEVEL,
  PAYOFF_CALLABILITY_BARRIER_STEP_SIZE,
  PAYOFF_CALLABILITY_BARRIER_TYPE,
  PAYOFF_CALLABILITY_CALL_EVENT_TYPE,
  PAYOFF_CALLABILITY_DEFERRED_PERIODS,
  PAYOFF_CALLABILITY_FREQUENCY,
  PAYOFF_CALLABILITY_LAST_PERIOD_CALLABLE,
  PAYOFF_CALLABILITY_OBS_FREQUENCY,
  PAYOFF_CALLABILITY_SCHEDULE,
  PAYOFF_CALLABILITY_TOUCH_BREAK,
  PAYOFF_PARTICIPATION_UPSIDE_TYPE,
  SUMMARY_PRODUCT_TYPE,
} from './product-capture-field-selectors.consts';

import { ProductTypeEnum } from 'src/app/product-capture/model/enums/product-capture.enums';
import { FCFieldEffect, FCSelector } from '../../model/form-config.interface';
import { UPSIDE_TYPE_KEYS } from 'src/app/product-capture/model/consts/upside-type.const';

const CLEAR_BARRIER_EFFECT: FCFieldEffect = {
  formControlNames: [PAYOFF_CALLABILITY_BARRIER_TYPE],
  selectValues: [null],
};
const DEFAULT_LAST_PERIOD_CALLABLE: FCFieldEffect = {
  formControlNames: [PAYOFF_CALLABILITY_LAST_PERIOD_CALLABLE],
  selectValues: [true],
};

export const CALL_EVENT_TYPE: FCSelector = {
  formControlNames: [PAYOFF_CALLABILITY_CALL_EVENT_TYPE],
  options: [
    {
      id: 'NONE',
      label: 'None',
      effects: [
        {
          formControlNames: [
            PAYOFF_CALLABILITY_FREQUENCY,
            PAYOFF_CALLABILITY_DEFERRED_PERIODS,
            PAYOFF_CALLABILITY_SCHEDULE,
            PAYOFF_CALLABILITY_BARRIER_TYPE,
            PAYOFF_CALLABILITY_BARRIER_LEVEL,
            PAYOFF_CALLABILITY_BARRIER_STEP_SIZE,
            PAYOFF_CALLABILITY_OBS_FREQUENCY,
            PAYOFF_CALLABILITY_TOUCH_BREAK,
            PAYOFF_CALLABILITY_AMOUNT_TYPE,
            PAYOFF_CALLABILITY_AMOUNT_STEP_SIZE,
            PAYOFF_CALLABILITY_AMOUNT,
            PAYOFF_CALLABILITY_LAST_PERIOD_CALLABLE,
          ],
          hidden: true,
        },
        {
          formControlNames: [PAYOFF_CALLABILITY_OBS_FREQUENCY],
          selectValues: [null],
        },
        {
          formControlNames: [EVENT_SCHEDULE_CALL_NOTICE_LAG],
          hidden: true,
          selectValues: [null],
        },
        { ...CLEAR_BARRIER_EFFECT },
      ],
    },
    {
      id: 'AUTOCALL',
      label: 'Autocallable',
      effects: [
        {
          formControlNames: [
            PAYOFF_CALLABILITY_FREQUENCY,
            PAYOFF_CALLABILITY_DEFERRED_PERIODS,
            PAYOFF_CALLABILITY_LAST_PERIOD_CALLABLE,
            PAYOFF_CALLABILITY_SCHEDULE,
            PAYOFF_CALLABILITY_BARRIER_TYPE,
            PAYOFF_CALLABILITY_TOUCH_BREAK,
            PAYOFF_CALLABILITY_AMOUNT_TYPE,
            PAYOFF_CALLABILITY_AMOUNT,
          ],
          hidden: false,
        },
        {
          formControlNames: [PAYOFF_CALLABILITY_OBS_FREQUENCY],
          selectValues: ['PERIOD_END'],
          hidden: false,
        },
        {
          formControlNames: [EVENT_SCHEDULE_CALL_NOTICE_LAG],
          hidden: true,
          selectValues: [null],
        },
        { ...DEFAULT_LAST_PERIOD_CALLABLE },
      ],
    },
    {
      id: 'ISSUER_CALL',
      label: 'Issuer Callable',
      effects: [
        {
          formControlNames: [
            PAYOFF_CALLABILITY_FREQUENCY,
            PAYOFF_CALLABILITY_DEFERRED_PERIODS,
            PAYOFF_CALLABILITY_LAST_PERIOD_CALLABLE,
            PAYOFF_CALLABILITY_SCHEDULE,
            PAYOFF_CALLABILITY_AMOUNT,
            PAYOFF_CALLABILITY_AMOUNT_TYPE,
            EVENT_SCHEDULE_CALL_NOTICE_LAG,
          ],
          hidden: false,
        },
        {
          formControlNames: [PAYOFF_CALLABILITY_OBS_FREQUENCY],
          selectValues: ['PERIOD_END'],
          hidden: true,
        },
        {
          formControlNames: [
            PAYOFF_CALLABILITY_BARRIER_TYPE,
            PAYOFF_CALLABILITY_BARRIER_LEVEL,
            PAYOFF_CALLABILITY_BARRIER_STEP_SIZE,
            PAYOFF_CALLABILITY_TOUCH_BREAK,
          ],
          hidden: true,
        },
        { ...CLEAR_BARRIER_EFFECT },
        { ...DEFAULT_LAST_PERIOD_CALLABLE },
      ],
      concatFieldRules: [
        {
          formControlNames: [SUMMARY_PRODUCT_TYPE],
          options: [
            {
              id: ProductTypeEnum.PRDC,
              effects: [
                {
                  formControlNames: [PAYOFF_CALLABILITY_AMOUNT],
                  hidden: false,
                  selectValues: [
                    {
                      value: '100%',
                      minValue: null,
                      maxValue: null,
                      indicativeValue: null,
                    },
                  ],
                },
                {
                  formControlNames: [PAYOFF_CALLABILITY_AMOUNT_TYPE],
                  hidden: false,
                  selectValues: ['FLAT'],
                },
              ],
            },
          ],
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_TYPE],
          options: [
            {
              id: UPSIDE_TYPE_KEYS.KNOCK_OUT_CALL_WAVE_UNLIMITED,
              effects: [
                {
                  formControlNames: [
                    PAYOFF_CALLABILITY_AMOUNT_TYPE,
                    PAYOFF_CALLABILITY_AMOUNT,
                  ],
                  hidden: true,
                  readOnly: false,
                },
              ],
            },
            {
              id: UPSIDE_TYPE_KEYS.KNOCK_OUT_PUT_WAVE_UNLIMITED,
              effects: [
                {
                  formControlNames: [
                    PAYOFF_CALLABILITY_AMOUNT_TYPE,
                    PAYOFF_CALLABILITY_AMOUNT,
                  ],
                  hidden: true,
                  readOnly: false,
                },
              ],
            },
            {
              id: UPSIDE_TYPE_KEYS.KNOCK_OUT_CALL_WAVE_XXL,
              effects: [
                {
                  formControlNames: [
                    PAYOFF_CALLABILITY_AMOUNT_TYPE,
                    PAYOFF_CALLABILITY_AMOUNT,
                  ],
                  hidden: true,
                  readOnly: false,
                },
              ],
            },
            {
              id: UPSIDE_TYPE_KEYS.KNOCK_OUT_PUT_WAVE_XXL,
              effects: [
                {
                  formControlNames: [
                    PAYOFF_CALLABILITY_AMOUNT_TYPE,
                    PAYOFF_CALLABILITY_AMOUNT,
                  ],
                  hidden: true,
                  readOnly: false,
                },
              ],
            },
          ],
        },
      ],
    },
    {
      id: '',
      effects: [
        {
          formControlNames: [
            PAYOFF_CALLABILITY_FREQUENCY,
            PAYOFF_CALLABILITY_DEFERRED_PERIODS,
            PAYOFF_CALLABILITY_LAST_PERIOD_CALLABLE,
            PAYOFF_CALLABILITY_SCHEDULE,
            PAYOFF_CALLABILITY_BARRIER_TYPE,
            PAYOFF_CALLABILITY_BARRIER_LEVEL,
            PAYOFF_CALLABILITY_BARRIER_STEP_SIZE,
            PAYOFF_CALLABILITY_OBS_FREQUENCY,
            PAYOFF_CALLABILITY_TOUCH_BREAK,
            PAYOFF_CALLABILITY_AMOUNT_TYPE,
            PAYOFF_CALLABILITY_AMOUNT,
            EVENT_SCHEDULE_CALL_NOTICE_LAG,
          ],
          hidden: false,
        },
        {
          formControlNames: [PAYOFF_CALLABILITY_OBS_FREQUENCY],
          selectValues: [null],
        },
        { ...CLEAR_BARRIER_EFFECT },
      ],
    },
  ],
};
